import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/App';
import axios from 'axios';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import Header from '@/components/Header';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ArrowLeft, GitCompareArrows, TrendingUp, Utensils, Skull, FileSpreadsheet,
  X, Download, FileText, Trophy, AlertTriangle,
} from 'lucide-react';
import HelpBenefits, { HELP_URLS } from '@/components/HelpBenefits';
import AdminHiddenSectionsOverview from '@/components/AdminHiddenSectionsOverview';
import FeatureVideoHero from '@/components/FeatureVideoHero';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import ProfileOverlayChart from '@/components/multiProfile/ProfileOverlayChart';
import RecordSelectorEnhanced from '@/components/multiProfile/RecordSelectorEnhanced';
import {
  PROFILE_COLORS,
  computeProfileSummaries, computeLeaders,
  buildGrowthChartData, buildSmoothGrowthChartData, buildFeedChartData, buildMortalityChartData,
  buildPdfNarrative, summariesToCsv,
  detectSpeciesMismatch, detectInputWeightSpread,
  loadSavedViews, saveSavedView, deleteSavedView,
  disambiguateProfileNames,
} from '@/utils/multiProfileAnalysis';
import { formatMissingFishRange } from '@/utils/reportFormatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ACTIONABILITY_STYLES = {
  ACTIONABLE: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  VALIDATION_REQUIRED: 'bg-amber-100 text-amber-700 border-amber-200',
  MONITOR_ONLY: 'bg-slate-200 text-slate-600 border-slate-300',
};
// ★ M2 — use the canonical long-form decision labels so Multi-Profile matches
//   the wording used on Spreadsheet / Snapshot / Growth-vs-Model.
const ACTIONABILITY_LABEL = {
  ACTIONABLE: 'ACTIONABLE',
  VALIDATION_REQUIRED: 'VALIDATION REQUIRED',
  MONITOR_ONLY: 'MONITOR ONLY',
};

// Display helper — all average weights render with 1 decimal place.
const fmtWeight = (w) => {
  const n = Number(w);
  if (!isFinite(n)) return '—';
  return `${n.toFixed(1)}g`;
};
const fmtWeightBare = (w) => {
  const n = Number(w);
  if (!isFinite(n)) return '—';
  return n.toFixed(1);
};

export default function MultiProfileComparison() {
  const navigate = useNavigate();
  const { user, token } = useAuth();
  const [loading, setLoading] = useState(false);
  const [savedRecords, setSavedRecords] = useState([]);
  const [selectedRecordIds, setSelectedRecordIds] = useState([]);
  const [selectedRecords, setSelectedRecords] = useState([]);
  const [showRecordSelector, setShowRecordSelector] = useState(true);
  const [exportingPdf, setExportingPdf] = useState(false);
  const pdfContentRef = useRef(null);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const isDemo = user?.is_demo === true;

  // Saved views
  const userKey = user?.email || user?.id || 'anon';
  const [savedViews, setSavedViews] = useState(() => loadSavedViews(userKey));

  // Fetch saved records
  useEffect(() => {
    const fetchRecords = async () => {
      try {
        setLoading(true);
        if (!token) { navigate('/auth'); return; }
        const response = await axios.get(
          `${BACKEND_URL}/api/spreadsheet/records`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        setSavedRecords(response.data || []);
      } catch {
        toast.error('Failed to load saved records');
      } finally {
        setLoading(false);
      }
    };
    fetchRecords();
  }, [navigate, token]);

  const toggleRecordSelection = useCallback((recordId) => {
    setSelectedRecordIds(prev => {
      if (prev.includes(recordId)) return prev.filter(id => id !== recordId);
      if (prev.length >= 10) { toast.error('Maximum 10 profiles allowed'); return prev; }
      return [...prev, recordId];
    });
  }, []);

  const loadSelectedRecords = useCallback(async () => {
    if (selectedRecordIds.length < 2) { toast.error('Select at least 2 records to compare'); return; }
    try {
      setLoading(true);
      const loadedRecords = [];
      for (const recordId of selectedRecordIds) {
        const response = await axios.get(
          `${BACKEND_URL}/api/spreadsheet/records/${recordId}`,
          { headers: { 'Authorization': `Bearer ${token}` } }
        );
        loadedRecords.push(response.data);
      }
      setSelectedRecords(loadedRecords);
      setShowRecordSelector(false);
      toast.success(`Loaded ${loadedRecords.length} profiles`);
    } catch {
      toast.error('Failed to load record details');
    } finally {
      setLoading(false);
    }
  }, [selectedRecordIds, token]);

  const clearSelection = useCallback(() => {
    setSelectedRecords([]);
    setSelectedRecordIds([]);
    setShowRecordSelector(true);
  }, []);

  // Remove a single profile from the active comparison (non-destructive)
  const removeProfileFromComparison = useCallback((id) => {
    setSelectedRecordIds(prev => prev.filter(x => x !== id));
    setSelectedRecords(prev => prev.filter(r => r.id !== id));
  }, []);

  const confirmDeleteRecord = useCallback((e, recordId) => {
    e.stopPropagation();
    setPendingDeleteId(recordId);
    setDeleteConfirmOpen(true);
  }, []);

  const handleDeleteRecord = useCallback(async () => {
    if (!pendingDeleteId) return;
    try {
      await axios.delete(
        `${BACKEND_URL}/api/spreadsheet/records/${pendingDeleteId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );
      setSavedRecords(prev => prev.filter(r => r.id !== pendingDeleteId));
      setSelectedRecordIds(prev => prev.filter(id => id !== pendingDeleteId));
      toast.success('Record deleted');
    } catch {
      toast.error('Failed to delete record');
    }
    setPendingDeleteId(null);
  }, [pendingDeleteId, token]);

  // Saved view handlers
  const handleSaveView = useCallback(({ name, recordIds }) => {
    const next = saveSavedView(userKey, { name, recordIds });
    setSavedViews(next);
    toast.success(`View "${name}" saved`);
  }, [userKey]);

  const handleDeleteView = useCallback((viewId) => {
    setSavedViews(deleteSavedView(userKey, viewId));
  }, [userKey]);

  const handleApplyView = useCallback((view) => {
    if (!view?.recordIds?.length) return;
    const valid = view.recordIds.filter(id => savedRecords.find(r => r.id === id));
    setSelectedRecordIds(valid);
    toast.info(`Applied view "${view.name}" — ${valid.length} of ${view.recordIds.length} records available`);
  }, [savedRecords]);

  // ── Derived data ────────────────────────────────────────────────────────
  // PFD-BUG-011 — Recorded (default) vs PFD-Adjusted Performance mode.
  // Must be declared BEFORE the `summaries` useMemo below so the memo's
  // dep array can read it without hitting Temporal Dead Zone on first
  // render. Only population-dependent metrics (bFCR, closing biomass,
  // feed efficiency) swap `input_number → input_number − saved_analysis
  // .missing_fish`. All recorded values (feed, mortality, harvests,
  // sample weights, SGR, mortality %) are preserved AS-IS.
  const [performanceMode, setPerformanceMode] = useState('recorded');

  // Disambiguate any duplicate profile names (e.g. two records both named
  // "Seabass example") BEFORE feeding downstream consumers — otherwise the
  // chart de-duplicates by name and one of the lines is silently dropped.
  // KPI cards, legend, tooltips, CSV/PDF exports all read from this list.
  const displayRecords = useMemo(
    () => disambiguateProfileNames(selectedRecords),
    [selectedRecords]
  );
  const summaries = useMemo(
    () => computeProfileSummaries(displayRecords, { mode: performanceMode }),
    [displayRecords, performanceMode],
  );
  // PFD-BUG-011 — Hoisted above `summaries` useMemo so React's TDZ doesn't
  // throw on first render. Values: 'recorded' (default) | 'adjusted'.
  // eslint-disable-next-line no-use-before-define
  const leaders = useMemo(() => computeLeaders(summaries), [summaries]);
  const speciesMismatch = useMemo(() => detectSpeciesMismatch(displayRecords), [displayRecords]);
  const weightSpread = useMemo(() => detectInputWeightSpread(displayRecords), [displayRecords]);
  const seriesKeys = useMemo(
    () => summaries.map(s => ({ key: s.name, color: s.color.stroke })),
    [summaries]
  );

  // Growth chart — PRD: Multi-Profile uses Days axis only (no %Cycle, no Normalize,
  // no IQR ribbon). These options are intentionally removed to keep the comparison
  // simple and decision-focused for the launch build.
  // Smooth Curve toggle (OFF by default): renders the same series but
  // re-anchored on real sample weights via monotonic-cubic (PCHIP)
  // interpolation — VISUAL only, no data mutations. See
  // `buildSmoothGrowthChartData` in `@/utils/multiProfileAnalysis`.
  const [smoothCurve, setSmoothCurve] = useState(false);
  const growthData = useMemo(
    () => smoothCurve
      ? buildSmoothGrowthChartData(displayRecords)
      : buildGrowthChartData(displayRecords, { axisMode: 'days', normalizeToInput: false }),
    [displayRecords, smoothCurve]
  );
  const feedData = useMemo(() => buildFeedChartData(displayRecords), [displayRecords]);
  const mortalityData = useMemo(() => buildMortalityChartData(displayRecords), [displayRecords]);

  // ── PDF / CSV export ────────────────────────────────────────────────────
  const handleExportCsv = useCallback(() => {
    if (!summaries.length) return;
    const csv = summariesToCsv(summaries, { mode: performanceMode });
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const modeSuffix = performanceMode === 'adjusted' ? '_pfd-adjusted' : '';
    link.download = `multi_profile_comparison${modeSuffix}_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    setTimeout(() => URL.revokeObjectURL(url), 60000);
    toast.success('CSV downloaded');
  }, [summaries, performanceMode]);

  const handleExportPdf = useCallback(async () => {
    if (selectedRecords.length === 0) { toast.error('No profiles selected'); return; }
    setExportingPdf(true);
    toast.info('Generating PDF report...');
    try {
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 10;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = margin;

      pdf.setFillColor(8, 145, 178);
      pdf.rect(0, 0, pageWidth, 26, 'F');
      pdf.setFontSize(16);
      pdf.setTextColor(255, 255, 255);
      pdf.text('Multi-Profile Comparison Report', pageWidth / 2, 11, { align: 'center' });
      pdf.setFontSize(9);
      pdf.text(`Generated: ${new Date().toLocaleString()}`, pageWidth / 2, 19, { align: 'center' });
      yPos = 32;

      // PFD-BUG-011 — label the active mode so the PDF is self-describing.
      pdf.setFontSize(10);
      pdf.setTextColor(55, 65, 81);
      pdf.setFont(undefined, 'bold');
      const modeLabel = performanceMode === 'adjusted'
        ? 'Mode: PFD-Adjusted Performance (bFCR / closing biomass / feed efficiency use saved Missing Fish estimates)'
        : 'Mode: Recorded Performance (all metrics as reported)';
      pdf.text(pdf.splitTextToSize(modeLabel, contentWidth), margin, yPos);
      yPos += 6;
      pdf.setFont(undefined, 'normal');

      // Narrative
      const narrative = buildPdfNarrative(summaries, leaders, {
        speciesMismatch: speciesMismatch.mixed,
        wideInputSpread: weightSpread.wideSpread,
      });
      if (narrative.length) {
        pdf.setFontSize(10);
        pdf.setTextColor(8, 145, 178);
        pdf.setFont(undefined, 'bold');
        pdf.text('Takeaways', margin, yPos);
        yPos += 5;
        pdf.setFontSize(9);
        pdf.setTextColor(55, 65, 81);
        pdf.setFont(undefined, 'normal');
        narrative.forEach(line => {
          const wrapped = pdf.splitTextToSize(`• ${line}`, contentWidth);
          pdf.text(wrapped, margin, yPos);
          yPos += wrapped.length * 4 + 1;
        });
        yPos += 3;
      }

      pdf.setFontSize(11);
      pdf.setTextColor(8, 145, 178);
      pdf.setFont(undefined, 'bold');
      pdf.text('Profile Summary', margin, yPos);
      yPos += 6;

      const cols = ['Profile', 'Input Wt', 'Latest Wt', 'bFCR', 'Feed (kg)', 'Mortality', 'Days', 'Decision'];
      const colWidths = [50, 20, 20, 16, 24, 22, 14, 24];
      pdf.setFillColor(240, 240, 240);
      pdf.rect(margin, yPos - 4, contentWidth, 7, 'F');
      pdf.setFontSize(7);
      pdf.setTextColor(80, 80, 80);
      pdf.setFont(undefined, 'bold');
      let xPos = margin + 2;
      cols.forEach((col, i) => { pdf.text(col, xPos, yPos); xPos += colWidths[i]; });
      yPos += 5;

      pdf.setFont(undefined, 'normal');
      summaries.forEach((s) => {
        if (yPos > pageHeight - 20) { pdf.addPage(); yPos = margin; }
        pdf.setTextColor(55, 65, 81);
        xPos = margin + 2;
        const row = [
          (s.name || '').substring(0, 28),
          fmtWeight(s.inputWeight),
          fmtWeight(s.latestWeight),
          s.accBFCR != null ? s.accBFCR.toFixed(2) : 'N/A',
          s.totalFeed.toLocaleString(),
          `${s.totalMortality.toLocaleString()} (${s.mortalityPct}%)`,
          `${s.daysInWater}`,
          ACTIONABILITY_LABEL[s.actionability] || '—',
        ];
        row.forEach((val, i) => { pdf.text(String(val), xPos, yPos); xPos += colWidths[i]; });
        yPos += 5;
      });
      yPos += 6;

      if (pdfContentRef.current) {
        const element = pdfContentRef.current;
        const originalStyle = element.style.cssText;
        element.style.width = '1200px';
        element.style.minWidth = '1200px';
        element.style.maxWidth = '1200px';
        element.style.backgroundColor = '#ffffff';
        window.dispatchEvent(new Event('resize'));
        await new Promise(resolve => setTimeout(resolve, 600));

        const canvas = await html2canvas(element, {
          scale: 2, useCORS: true, logging: false, backgroundColor: '#ffffff',
          scrollX: 0, scrollY: -window.scrollY,
        });
        element.style.cssText = originalStyle;
        window.dispatchEvent(new Event('resize'));

        const imgData = canvas.toDataURL('image/png');
        const ratio = contentWidth / canvas.width;
        const totalHeight = canvas.height * ratio;
        const availableHeight = pageHeight - yPos - 10;
        if (totalHeight <= availableHeight) {
          pdf.addImage(imgData, 'PNG', margin, yPos, contentWidth, totalHeight);
        } else {
          const firstPageHeight = availableHeight;
          const srcHeight1 = Math.floor(canvas.height * (firstPageHeight / totalHeight));
          const tmp1 = document.createElement('canvas');
          tmp1.width = canvas.width; tmp1.height = srcHeight1;
          tmp1.getContext('2d').drawImage(canvas, 0, 0, canvas.width, srcHeight1, 0, 0, canvas.width, srcHeight1);
          pdf.addImage(tmp1.toDataURL('image/png'), 'PNG', margin, yPos, contentWidth, firstPageHeight);
          let consumed = srcHeight1;
          while (consumed < canvas.height) {
            pdf.addPage();
            const remaining = canvas.height - consumed;
            const pageAvail = pageHeight - margin * 2;
            const chunkH = Math.min(remaining, Math.floor(canvas.height * (pageAvail / totalHeight)));
            const tmp = document.createElement('canvas');
            tmp.width = canvas.width; tmp.height = chunkH;
            tmp.getContext('2d').drawImage(canvas, 0, consumed, canvas.width, chunkH, 0, 0, canvas.width, chunkH);
            const scaledH = chunkH * ratio;
            pdf.addImage(tmp.toDataURL('image/png'), 'PNG', margin, margin, contentWidth, Math.min(scaledH, pageAvail));
            consumed += chunkH;
          }
        }
      }

      const totalPages = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        pdf.setPage(i);
        pdf.setFontSize(7);
        pdf.setTextColor(150, 150, 150);
        pdf.text(`Precision Fish Detection - Multi-Profile Comparison | Page ${i}/${totalPages}`, pageWidth / 2, pageHeight - 5, { align: 'center' });
      }

      const fileName = `multi_profile_comparison_${new Date().toISOString().split('T')[0]}.pdf`;
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      if (isMobile) {
        const blob = pdf.output('blob');
        const blobUrl = URL.createObjectURL(blob);
        window.open(blobUrl, '_blank');
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
      } else {
        pdf.save(fileName);
      }
      toast.success('PDF exported');
    } catch (err) {
      toast.error('Failed to export PDF');
    } finally {
      setExportingPdf(false);
    }
  }, [selectedRecords, summaries, leaders, speciesMismatch, weightSpread]);

  // ── Render ──────────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F3F4F6] dark:bg-[#0b1220] transition-colors duration-200" data-testid="multi-profile-page">
      <Header />
      <div className="max-w-7xl mx-auto p-4 sm:p-6">
        <div className="flex items-center gap-3 mb-6">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Back
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-[#0891b2] flex items-center gap-2">
              <GitCompareArrows className="w-6 h-6" />
              Multi-Profile Comparison
            </h1>
            <p className="text-sm text-gray-500">Compare growth, feed, and mortality across saved spreadsheet uploads</p>
          </div>
        </div>

        {/* ★ Above-the-fold feature video — PRD item 1 */}
        <FeatureVideoHero
          featureName="Multi-Profile Comparison"
          youtubeUrl={HELP_URLS?.multiProfileComparison?.youtube}
        />

        {/* Active profile chips (visible when viewing comparison) */}
        {!showRecordSelector && selectedRecords.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="profile-chips">
            {summaries.map(s => (
              <div
                key={s.id}
                className="inline-flex items-center gap-1.5 rounded-full border border-slate-200 bg-white pl-1 pr-0.5 py-0.5 text-xs shadow-sm"
                data-testid={`profile-chip-${s.id}`}
              >
                <span className="inline-block w-3 h-3 rounded-full" style={{ backgroundColor: s.color.stroke }} />
                <span className="text-slate-700 font-medium truncate max-w-[180px]" title={s.name}>{s.name}</span>
                <button
                  className="text-slate-400 hover:text-rose-600 h-4 w-4 inline-flex items-center justify-center rounded-full"
                  onClick={() => removeProfileFromComparison(s.id)}
                  title="Remove from this comparison (does not delete)"
                  data-testid={`remove-profile-chip-${s.id}`}
                >
                  <X className="w-2.5 h-2.5" />
                </button>
              </div>
            ))}
            <Button variant="outline" size="sm" onClick={() => setShowRecordSelector(true)} data-testid="change-selection-btn">
              Change selection
            </Button>
          </div>
        )}

        {showRecordSelector && (
          <>
            <RecordSelectorEnhanced
              savedRecords={savedRecords}
              loading={loading}
              selectedRecordIds={selectedRecordIds}
              onToggleSelection={toggleRecordSelection}
              onConfirmDelete={confirmDeleteRecord}
              onNavigateToUploader={() => navigate('/spreadsheet-comparison')}
              isDemo={isDemo || user?.is_read_only === true}
              profileColors={PROFILE_COLORS}
              savedViews={savedViews}
              onSaveView={handleSaveView}
              onDeleteView={handleDeleteView}
              onApplyView={handleApplyView}
              onLoadSelected={loadSelectedRecords}
              onClearSelection={clearSelection}
            />
          </>
        )}

        {!showRecordSelector && selectedRecords.length > 0 && (
          <div className="space-y-4">
            {/* Cross-comparison warnings (P0 #2) */}
            {(speciesMismatch.mixed || weightSpread.wideSpread) && (
              <div className="rounded-lg border border-amber-300 bg-amber-50 p-3 flex items-start gap-2" data-testid="cross-comparison-warning">
                <AlertTriangle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-xs text-amber-800 space-y-0.5">
                  {speciesMismatch.mixed && (
                    <p data-testid="species-mismatch-note">
                      <span className="font-semibold">Cross-species comparison:</span> {speciesMismatch.species.join(', ')}. Metrics are not directly equivalent — use for directional insight only.
                    </p>
                  )}
                  {weightSpread.wideSpread && (
                    <p data-testid="weight-spread-note">
                      <span className="font-semibold">Input weights vary by ≥30%</span> ({weightSpread.min}g – {weightSpread.max}g). Growth curves start from different baselines — interpret early-cycle differences with care.
                    </p>
                  )}
                </div>
              </div>
            )}

            {/* Toolbar — sticky, consolidated */}
            <div className="sticky top-0 z-10 -mx-4 sm:-mx-6 px-4 sm:px-6 py-2 bg-slate-50/80 backdrop-blur border-b border-slate-200 flex items-center justify-between flex-wrap gap-2" data-pdf-hide data-testid="mpc-toolbar">
              <div className="flex items-center gap-2">
                <h2 className="text-base font-semibold text-gray-800" data-testid="toolbar-title">
                  Comparing <span className="text-[#0891b2] tabular-nums">{selectedRecords.length}</span> Profiles
                </h2>
                <span className="text-[11px] text-slate-400 hidden sm:inline">
                  · {speciesMismatch.mixed ? 'Mixed species' : (speciesMismatch.species[0] || '—')}
                </span>
              </div>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    size="sm"
                    className="bg-[#0891b2] hover:bg-[#0e7490] text-white"
                    disabled={exportingPdf}
                    data-testid="export-menu-trigger"
                  >
                    <Download className="w-4 h-4 mr-1.5" /> {exportingPdf ? 'Generating…' : 'Export'}
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-44">
                  {user?.is_admin && (
                    <DropdownMenuItem onClick={handleExportPdf} data-testid="export-pdf-item">
                      <Download className="w-3.5 h-3.5 mr-2" /> PDF report
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuItem onClick={handleExportCsv} data-testid="export-csv-item">
                    <FileText className="w-3.5 h-3.5 mr-2" /> CSV summary
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>

            {/* Summary cards with leader trophies + SSOT badges */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
              {summaries.map((stat) => {
                const trophies = [];
                if (leaders.bFCR === stat.id) trophies.push('bFCR');
                if (leaders.mortality === stat.id) trophies.push('Mortality');
                if (leaders.sgr === stat.id) trophies.push('SGR');
                if (leaders.feedEfficiency === stat.id) trophies.push('Efficiency');
                return (
                  <Card
                    key={stat.id}
                    className="border-l-4"
                    style={{ borderLeftColor: stat.color.stroke }}
                    data-testid={`summary-card-${stat.id}`}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between mb-2 gap-2">
                        <h3 className="text-sm font-semibold text-gray-800 truncate flex-1" title={stat.name}>{stat.name}</h3>
                        {stat.actionability && (
                          <span
                            className={`text-[9px] font-medium px-1.5 py-0.5 rounded border ${ACTIONABILITY_STYLES[stat.actionability] || 'bg-slate-100 text-slate-500'}`}
                            data-testid={`actionability-chip-${stat.id}`}
                          >
                            {ACTIONABILITY_LABEL[stat.actionability] || stat.actionability}
                          </span>
                        )}
                      </div>
                      {trophies.length > 0 && (
                        <div className="flex flex-wrap gap-1 mb-2" data-testid={`leader-chips-${stat.id}`}>
                          {trophies.map(t => (
                            <span key={t} className="inline-flex items-center gap-0.5 text-[9px] font-semibold px-1.5 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200">
                              <Trophy className="w-2.5 h-2.5" /> {t}
                            </span>
                          ))}
                        </div>
                      )}
                      {stat.hasExtremeFCR && (
                        <div className="text-[10px] text-rose-700 bg-rose-50 border border-rose-200 rounded px-1.5 py-0.5 mb-2 inline-flex items-center gap-1" data-testid={`extreme-fcr-flag-${stat.id}`}>
                          <AlertTriangle className="w-2.5 h-2.5" /> FCR outliers in data
                        </div>
                      )}
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between"><span className="text-gray-500">Input Weight:</span><span className="font-medium">{fmtWeight(stat.inputWeight)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Latest Weight:</span><span className="font-medium">{fmtWeight(stat.latestWeight)}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Days in Water:</span><span className="font-medium">{stat.daysInWater}</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Total Feed:</span><span className="font-medium">{stat.totalFeed.toLocaleString()} kg</span></div>
                        <div className="flex justify-between"><span className="text-gray-500">Mortality:</span><span className="font-medium">{stat.totalMortality.toLocaleString()} ({stat.mortalityPct}%)</span></div>
                        <div className="flex justify-between">
                          <span className="text-gray-500">bFCR:</span>
                          <span className="font-medium">
                            {stat.accBFCR != null ? (
                              <span className={`px-1.5 py-0.5 rounded text-xs font-semibold ${
                                stat.accBFCR < 1.6 ? 'bg-green-100 text-green-700'
                                : stat.accBFCR < 2.0 ? 'bg-amber-100 text-amber-700'
                                : 'bg-rose-100 text-rose-700'
                              }`}>{stat.accBFCR.toFixed(2)}</span>
                            ) : stat.mode === 'adjusted' && stat.mfState === 'unavailable' ? (
                              <span className="text-slate-400 text-xs" title="No saved Missing Fish estimate">— <span className="text-[9px]">no estimate</span></span>
                            ) : stat.mode === 'adjusted' && stat.mfState === 'invalid' ? (
                              <span className="text-rose-400 text-xs" title="Missing Fish exceeds stocked count">— <span className="text-[9px]">invalid</span></span>
                            ) : <span className="text-gray-400">N/A</span>}
                          </span>
                        </div>
                        {stat.sgr != null && (
                          <div className="flex justify-between"><span className="text-gray-500">SGR (%/day):</span><span className="font-medium">{stat.sgr.toFixed(2)}</span></div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            {/* PDF capture section */}
            <div ref={pdfContentRef} className="space-y-4">
              {/* Growth chart — per PRD: Days-only axis, no Normalize / IQR / % Cycle for Multi-Profile */}
              <Card data-testid="growth-chart-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-gray-800 flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-[#0891b2]" /> Growth Comparison
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {growthData.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">No growth data available.</div>
                  ) : (
                    <ProfileOverlayChart
                      data={growthData}
                      seriesKeys={seriesKeys}
                      xKey="day"
                      xLabel="Days since input"
                      yLabel="Weight (g)"
                      yFormatter={(v) => `${Number(v).toFixed(1)} g`}
                      showDistributionRibbon={false}
                      testId="growth-chart"
                    />
                  )}
                  {/* Smooth Curve toggle — VISUAL ONLY. Re-anchors each
                      profile on its actual sample weights via monotonic
                      cubic (PCHIP) interpolation. Curve passes exactly
                      through every sample, no overshoot, no artificial
                      peaks/dips. Does not modify any recorded data. */}
                  <div className="flex items-center justify-end gap-2 mt-2 pr-1">
                    <button
                      type="button"
                      onClick={() => setSmoothCurve(v => !v)}
                      aria-pressed={smoothCurve}
                      data-testid="smooth-curve-toggle"
                      className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md border transition-colors ${
                        smoothCurve
                          ? 'bg-[#0891b2] text-white border-[#0891b2] shadow-sm'
                          : 'bg-white text-slate-700 border-slate-300 hover:bg-slate-50'
                      }`}
                    >
                      <span className={`inline-block w-1.5 h-1.5 rounded-full ${smoothCurve ? 'bg-white' : 'bg-slate-400'}`} />
                      Smooth Curve{smoothCurve ? ' · ON' : ''}
                    </button>
                  </div>
                </CardContent>
              </Card>

              {/* Feed chart */}
              <Card data-testid="feed-chart-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-gray-800 flex items-center gap-2">
                    <Utensils className="w-5 h-5 text-amber-600" /> Accumulated Feed Comparison
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {feedData.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">No feed data available.</div>
                  ) : (
                    <ProfileOverlayChart
                      data={feedData}
                      seriesKeys={seriesKeys}
                      xLabel="Days since input"
                      yLabel="Feed (kg)"
                      testId="feed-chart"
                    />
                  )}
                </CardContent>
              </Card>

              {/* Mortality chart */}
              <Card data-testid="mortality-chart-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-gray-800 flex items-center gap-2">
                    <Skull className="w-5 h-5 text-rose-600" /> Accumulated Mortality Comparison
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {mortalityData.length === 0 ? (
                    <div className="text-center py-8 text-gray-500 text-sm">No mortality data available.</div>
                  ) : (
                    <ProfileOverlayChart
                      data={mortalityData}
                      seriesKeys={seriesKeys}
                      xLabel="Days since input"
                      yLabel="Mortality (fish)"
                      testId="mortality-chart"
                    />
                  )}
                </CardContent>
              </Card>

              {/* Differential A vs B — removed per user request */}

              {/* PFD-BUG-011 — Performance mode toggle above Detailed Comparison.
                  Default is Recorded. Adjusted mode uses saved_analysis.missing_fish
                  to swap input_number → adjusted fish count ONLY for bFCR, closing
                  biomass, and feed efficiency. Recorded feed / mortality / harvests
                  / sample weights / SGR / mortality % are preserved identically. */}
              <div
                data-testid="performance-mode-toolbar"
                data-pdf-hide
                className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 bg-white border border-slate-200 rounded-lg"
              >
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold text-gray-700 uppercase tracking-wide">
                    Performance mode
                  </span>
                  <div className="inline-flex rounded-md border border-slate-300 overflow-hidden">
                    <button
                      type="button"
                      aria-pressed={performanceMode === 'recorded'}
                      onClick={() => setPerformanceMode('recorded')}
                      data-testid="performance-mode-recorded"
                      className={`px-3 py-1 text-xs font-medium transition-colors ${
                        performanceMode === 'recorded'
                          ? 'bg-[#0891b2] text-white'
                          : 'bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      Recorded
                    </button>
                    <button
                      type="button"
                      aria-pressed={performanceMode === 'adjusted'}
                      onClick={() => setPerformanceMode('adjusted')}
                      data-testid="performance-mode-adjusted"
                      className={`px-3 py-1 text-xs font-medium border-l border-slate-300 transition-colors ${
                        performanceMode === 'adjusted'
                          ? 'bg-amber-500 text-white'
                          : 'bg-white text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      PFD-Adjusted
                    </button>
                  </div>
                </div>
                <div className="text-[11px] text-slate-500 flex items-center gap-2">
                  {performanceMode === 'adjusted' ? (
                    <>
                      <span
                        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded border border-amber-200 bg-amber-50 text-amber-800 font-medium"
                        data-testid="performance-mode-active-chip"
                      >
                        PFD-Adjusted active
                      </span>
                      <span data-testid="performance-mode-coverage">
                        {summaries.filter(s => s.mfState === 'ok').length} of {summaries.length} cages with valid Missing Fish
                      </span>
                    </>
                  ) : (
                    <span>bFCR / closing biomass / feed efficiency reflect the reported input fish count</span>
                  )}
                </div>
              </div>

              {/* Detailed comparison table — responsive: table on md+, cards below */}
              <Card data-testid="comparison-table-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base text-gray-800">Detailed Comparison</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm border-collapse">
                      <thead>
                        <tr className="border-b-2 border-slate-200">
                          <th className="text-left py-2 px-3 text-xs text-gray-500">Profile</th>
                          <th className="text-left py-2 px-3 text-xs text-gray-500">Decision</th>
                          <th className="text-right py-2 px-3 text-xs text-gray-500">Input Fish</th>
                          <th className="text-right py-2 px-3 text-xs text-gray-500">Input Wt</th>
                          <th className="text-right py-2 px-3 text-xs text-gray-500">Latest Wt</th>
                          <th className="text-right py-2 px-3 text-xs text-gray-500">Acc. bFCR</th>
                          <th className="text-right py-2 px-3 text-xs text-gray-500">SGR</th>
                          <th className="text-right py-2 px-3 text-xs text-gray-500">Feed</th>
                          <th className="text-right py-2 px-3 text-xs text-gray-500">Mortality</th>
                          <th className="text-right py-2 px-3 text-xs text-gray-500">Mort. %</th>
                          <th className="text-right py-2 px-3 text-xs text-gray-500">Days</th>
                          <th className="text-right py-2 px-3 text-xs text-gray-500">Missing Fish</th>
                        </tr>
                      </thead>
                      <tbody>
                        {summaries.map((stat) => (
                          <tr key={stat.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`comparison-row-${stat.id}`}>
                            <td className="py-2 px-3 flex items-center gap-2">
                              <div className="w-3 h-3 rounded-full" style={{ backgroundColor: stat.color.stroke }} />
                              <span className="font-medium">{stat.name}</span>
                              {stat.hasExtremeFCR && <AlertTriangle className="w-3 h-3 text-rose-500" title="FCR outliers in data" />}
                            </td>
                            <td className="py-2 px-3" data-testid={`comparison-decision-${stat.id}`}>
                              {stat.actionability ? (
                                <span className={`text-[10px] font-medium px-1.5 py-0.5 rounded border ${ACTIONABILITY_STYLES[stat.actionability] || ''}`}>
                                  {ACTIONABILITY_LABEL[stat.actionability] || stat.actionability}
                                </span>
                              ) : <span className="text-slate-300 text-xs" title="No MFI run cached — open this profile to populate">—</span>}
                            </td>
                            <td className="py-2 px-3 text-right font-mono">{stat.inputNumber?.toLocaleString()}</td>
                            <td className="py-2 px-3 text-right font-mono">{fmtWeight(stat.inputWeight)}</td>
                            <td className="py-2 px-3 text-right font-mono">{fmtWeight(stat.latestWeight)}</td>
                            <td className="py-2 px-3 text-right">
                              {stat.accBFCR != null ? (
                                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  stat.accBFCR < 1.6 ? 'bg-green-100 text-green-700'
                                  : stat.accBFCR < 2.0 ? 'bg-amber-100 text-amber-700'
                                  : 'bg-rose-100 text-rose-700'
                                }`} data-testid={`comparison-bfcr-${stat.id}`}>{stat.accBFCR.toFixed(2)}</span>
                              ) : stat.mode === 'adjusted' && stat.mfState === 'unavailable' ? (
                                <span className="text-slate-300 text-xs" title="No saved Missing Fish estimate — open this profile to populate" data-testid={`comparison-bfcr-${stat.id}`}>— <span className="text-[9px] text-slate-500">Analysis not run</span></span>
                              ) : stat.mode === 'adjusted' && stat.mfState === 'invalid' ? (
                                <span className="text-slate-300 text-xs" title="Missing Fish estimate exceeds stocked count — invalid for adjustment" data-testid={`comparison-bfcr-${stat.id}`}>— <span className="text-[9px] text-rose-500">Invalid estimate</span></span>
                              ) : <span className="text-gray-400 text-xs" data-testid={`comparison-bfcr-${stat.id}`}>N/A</span>}
                            </td>
                            <td className="py-2 px-3 text-right font-mono">{stat.sgr != null ? stat.sgr.toFixed(2) : '—'}</td>
                            <td className="py-2 px-3 text-right font-mono">{stat.totalFeed.toLocaleString()} kg</td>
                            <td className="py-2 px-3 text-right font-mono">{stat.totalMortality.toLocaleString()}</td>
                            <td className="py-2 px-3 text-right">
                              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                parseFloat(stat.mortalityPct) > 5 ? 'bg-rose-100 text-rose-700'
                                : parseFloat(stat.mortalityPct) > 2 ? 'bg-amber-100 text-amber-700'
                                : 'bg-green-100 text-green-700'
                              }`}>{stat.mortalityPct}%</span>
                            </td>
                            <td className="py-2 px-3 text-right font-mono">{stat.daysInWater}</td>
                            <td className="py-2 px-3 text-right font-mono" data-testid={`comparison-missing-${stat.id}`}>
                              {(() => {
                                // ★ H10 — PRD canonical missing-fish range column.
                                //   Reads the value cached on the record when the user last ran
                                //   Missing Fish Intelligence. When no MFI run has been recorded
                                //   yet the cell shows an em-dash so users know they need to open
                                //   the individual profile to populate it.
                                const mfCount = stat.missingFishCount;
                                if (mfCount == null) {
                                  return <span className="text-slate-300" title="No MFI run cached — open this profile to populate">—</span>;
                                }
                                const range = formatMissingFishRange({
                                  missingFishCount: mfCount,
                                  dataReliabilityScore: stat.dataReliabilityScore ?? 50,
                                });
                                if (range.isWithinMargin) {
                                  return <span className="text-emerald-700 text-xs">Within margin</span>;
                                }
                                return (
                                  <span className={range.direction === 'extra' ? 'text-emerald-700' : 'text-rose-700'}>
                                    {range.label}
                                  </span>
                                );
                              })()}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {/* Mobile — stacked cards */}
                  <div className="md:hidden space-y-2">
                    {summaries.map((stat) => (
                      <div key={stat.id} className="border border-slate-200 rounded-md p-2.5 text-xs" data-testid={`comparison-mobile-${stat.id}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-1.5">
                            <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stat.color.stroke }} />
                            <span className="font-semibold">{stat.name}</span>
                          </div>
                          {stat.actionability && (
                            <span className={`text-[9px] font-medium px-1.5 py-0.5 rounded border ${ACTIONABILITY_STYLES[stat.actionability] || ''}`}>
                              {ACTIONABILITY_LABEL[stat.actionability] || stat.actionability}
                            </span>
                          )}
                        </div>
                        <div className="grid grid-cols-2 gap-x-2 gap-y-0.5">
                          <div className="text-slate-500">bFCR: <span className="text-slate-800 font-medium">{stat.accBFCR != null ? stat.accBFCR.toFixed(2) : '—'}</span></div>
                          <div className="text-slate-500">SGR: <span className="text-slate-800 font-medium">{stat.sgr != null ? stat.sgr.toFixed(2) : '—'}</span></div>
                          <div className="text-slate-500">Feed: <span className="text-slate-800 font-medium">{stat.totalFeed.toLocaleString()} kg</span></div>
                          <div className="text-slate-500">Mort.: <span className="text-slate-800 font-medium">{stat.mortalityPct}%</span></div>
                          <div className="text-slate-500">Input: <span className="text-slate-800 font-medium">{fmtWeight(stat.inputWeight)}</span></div>
                          <div className="text-slate-500">Latest: <span className="text-slate-800 font-medium">{fmtWeight(stat.latestWeight)}</span></div>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        )}

        {/* Admin-only: Save / Discard report-visibility changes */}
        <AdminHiddenSectionsOverview className="mb-4" />

        <HelpBenefits
          youtubeUrl={HELP_URLS.multiProfileComparison.youtube}
          benefitsUrl={HELP_URLS.multiProfileComparison.benefits}
          featureName="Multi-Profile Comparison"
        />
      </div>

      <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Record</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to permanently delete this saved analysis? This cannot be undone. To remove a profile from the current comparison without deleting it, use the × on its chip instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="delete-confirm-btn"
              onClick={handleDeleteRecord}
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
